import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, Clock, Video, Gift, CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { registerWebinar, createWebinarUpsell, getWebinarUpsellStatus } from "@/lib/api";

const CHICAGO_TZ = "America/Chicago";

function chicagoWallToUtc(y, m, d, h, mi) {
  const target = Date.UTC(y, m, d, h, mi);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: CHICAGO_TZ, hour12: false,
    year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric",
  });
  let ts = target;
  for (let i = 0; i < 3; i++) {
    const p = Object.fromEntries(fmt.formatToParts(ts).filter((x) => x.type !== "literal").map((x) => [x.type, x.value]));
    const wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute);
    ts += target - wall;
  }
  return ts;
}

function nextWebinarTs() {
  const now = Date.now();
  const dateFmt = new Intl.DateTimeFormat("en-US", { timeZone: CHICAGO_TZ, year: "numeric", month: "numeric", day: "numeric" });
  const wdFmt = new Intl.DateTimeFormat("en-US", { timeZone: CHICAGO_TZ, weekday: "short" });
  const p = Object.fromEntries(dateFmt.formatToParts(now).filter((x) => x.type !== "literal").map((x) => [x.type, x.value]));
  const base = new Date(Date.UTC(+p.year, +p.month - 1, +p.day));
  for (let i = 0; i < 8; i++) {
    const day = new Date(base);
    day.setUTCDate(base.getUTCDate() + i);
    const ts = chicagoWallToUtc(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), 19, 0);
    if (wdFmt.format(ts) === "Wed" && ts > now) return ts;
  }
  return now;
}

function useWebinarCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    mins: Math.floor((diff / 60000) % 60),
    secs: Math.floor((diff / 1000) % 60),
  };
}

export default function Live() {
  const [params] = useSearchParams();
  const webinarTs = useMemo(() => nextWebinarTs(), []);
  const cd = useWebinarCountdown(webinarTs);
  const webinarLabel = useMemo(
    () => new Intl.DateTimeFormat("en-US", { timeZone: CHICAGO_TZ, weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" }).format(webinarTs) + " CT",
    [webinarTs]
  );

  const [form, setForm] = useState({ first_name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [upsellPaid, setUpsellPaid] = useState(false);

  useEffect(() => {
    const t0 = setTimeout(() => { document.title = "Free Live Webinar — Toriano Sanzone, The Wolfkeeper"; }, 50);
    return () => clearTimeout(t0);
  }, []);

  useEffect(() => {
    const upsell = params.get("upsell");
    const sessionId = params.get("session_id");
    if (upsell === "cancelled") {
      toast.error("Checkout cancelled — the consultation offer is still here when you're ready.");
    }
    if (upsell === "success" && sessionId) {
      let attempts = 0;
      const poll = async () => {
        try {
          const st = await getWebinarUpsellStatus(sessionId);
          if (st.payment_status === "paid") {
            setUpsellPaid(true);
            setRegistered(true);
            toast.success("Consultation locked in — check your email for next steps.");
            return;
          }
        } catch { /* retry */ }
        if (++attempts < 15) setTimeout(poll, 2000);
      };
      poll();
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await registerWebinar(form);
      setRegistered(true);
      toast.success(`You're in, ${form.first_name.trim()}! See you Wednesday.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Registration failed — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const buyUpsell = async () => {
    setUpsellLoading(true);
    try {
      const res = await createWebinarUpsell({
        origin_url: window.location.origin,
        email: form.email,
        first_name: form.first_name,
      });
      window.location.href = res.checkout_url;
    } catch {
      toast.error("Could not open checkout. Please try again.");
      setUpsellLoading(false);
    }
  };

  const countdown = [
    { v: cd.days, l: "Days" }, { v: cd.hours, l: "Hours" }, { v: cd.mins, l: "Min" }, { v: cd.secs, l: "Sec" },
  ];

  return (
    <div data-testid="webinar-page" className="min-h-screen bg-[#0a0908] text-[#f7f1e5]">
      <div className="max-w-md mx-auto px-5 pt-8 pb-16 sm:max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#e8c876]" data-testid="webinar-home-link">
            <span className="wolf-mark text-xl">TS</span>
            <span className="text-[10px] tracking-[0.28em] uppercase">Home</span>
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#c8102e] text-white px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase font-bold">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Free Webinar
          </span>
        </div>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-8">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#c9a24b]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            Live with Toriano Sanzone
          </div>
          <h1 className="mt-3 font-display text-[clamp(2rem,9vw,3.4rem)] leading-[0.98] tracking-[-0.02em]" data-testid="webinar-headline">
            The Wolfkeeper Method — <span className="italic text-[#e8c876]">Free Live Webinar</span>
          </h1>
          <p className="mt-4 text-[#f7f1e5]/75 text-sm sm:text-base leading-relaxed">
            Every Wednesday, Toriano goes live to break down the psychology of leadership — for your dog,
            your business, and your life. Bring your questions. Leave with a plan.
          </p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[11px] tracking-[0.18em] uppercase text-[#f7f1e5]/70"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            <span className="flex items-center gap-2"><CalendarDays size={13} className="text-[#e8c876]" /> {webinarLabel}</span>
            <span className="flex items-center gap-2"><Video size={13} className="text-[#e8c876]" /> Live online</span>
          </div>
        </motion.div>

        {/* Countdown */}
        <div className="mt-7" data-testid="webinar-countdown">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#f7f1e5]/50 mb-2 flex items-center gap-2">
            <Clock size={12} className="text-[#e8c876]" /> Next session begins in
          </div>
          <div className="grid grid-cols-4 gap-2">
            {countdown.map((c) => (
              <div key={c.l} className="rounded-2xl bg-white/5 border border-[#c9a24b]/25 px-2 py-3 text-center">
                <div className="stat-num text-2xl text-[#e8c876]">{String(c.v).padStart(2, "0")}</div>
                <div className="text-[9px] tracking-[0.24em] uppercase text-[#f7f1e5]/60 mt-0.5">{c.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Form / Confirmation */}
        {!registered ? (
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 rounded-3xl bg-[#f7f1e5] text-[#0a0908] p-6 sm:p-8 shadow-[0_30px_80px_-40px_rgba(201,162,75,0.4)]"
            data-testid="webinar-form"
          >
            <div className="text-[10px] tracking-[0.28em] uppercase text-black/50" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
              Reserve your free seat
            </div>
            <div className="mt-4 space-y-3">
              <input
                required
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                placeholder="First name"
                data-testid="webinar-input-first-name"
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-3.5 text-sm outline-none focus:border-[#c9a24b]"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email address"
                data-testid="webinar-input-email"
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-3.5 text-sm outline-none focus:border-[#c9a24b]"
              />
              <input
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Phone number"
                data-testid="webinar-input-phone"
                className="w-full rounded-xl border border-black/15 bg-white px-4 py-3.5 text-sm outline-none focus:border-[#c9a24b]"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="webinar-register-button"
              className="mt-5 w-full rounded-full py-4 text-[12px] tracking-[0.24em] uppercase font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }}
            >
              {submitting ? <Loader2 size={15} className="animate-spin" /> : <Gift size={15} />}
              {submitting ? "Reserving…" : "Claim My Free Seat"}
            </button>
            <p className="mt-3 text-center text-[11px] text-black/45">100% free. No card required.</p>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
            className="mt-8 rounded-3xl bg-[#f7f1e5] text-[#0a0908] p-6 sm:p-8"
            data-testid="webinar-confirmation"
          >
            <div className="flex items-center gap-3">
              <CheckCircle2 size={26} className="text-[#0f5d3a] shrink-0" />
              <h2 className="font-display text-2xl sm:text-3xl" data-testid="webinar-confirmation-title">
                You're in{form.first_name ? `, ${form.first_name}` : ""}!
              </h2>
            </div>
            <p className="mt-3 text-sm text-black/70 leading-relaxed">
              Your free seat is reserved for <strong>{webinarLabel}</strong>. Show up a few minutes early —
              the pack fills fast.
            </p>

            {/* Upsell */}
            {!upsellPaid ? (
              <div className="mt-6 rounded-2xl border-2 border-[#c9a24b] bg-[#0a0908] text-[#f7f1e5] p-5" data-testid="webinar-upsell">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#e8c876]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                  One-time offer for attendees
                </div>
                <div className="mt-2 flex items-end justify-between gap-3">
                  <h3 className="font-display text-xl leading-tight">15-Minute 1:1 Consultation with Toriano</h3>
                  <div className="text-right shrink-0">
                    <div className="stat-num text-3xl text-[#e8c876]">$19.99</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 line-through">$150 value</div>
                  </div>
                </div>
                <p className="mt-2 text-[13px] text-white/70 leading-relaxed">
                  Jump the line. Get direct eyes on your dog, your business, or your next move — before the webinar even starts.
                </p>
                <button
                  onClick={buyUpsell}
                  disabled={upsellLoading}
                  data-testid="webinar-upsell-button"
                  className="mt-4 w-full rounded-full py-3.5 text-[11px] tracking-[0.24em] uppercase font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ background: "linear-gradient(90deg,#e8c876,#c9a24b)", color: "#0a0908" }}
                >
                  {upsellLoading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {upsellLoading ? "Opening checkout…" : "Add My Consultation — $19.99"}
                </button>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl bg-[#0f5d3a] text-white p-5" data-testid="webinar-upsell-confirmed">
                <div className="flex items-center gap-2 font-bold text-sm tracking-wide">
                  <CheckCircle2 size={18} /> Consultation booked
                </div>
                <p className="mt-2 text-[13px] text-white/85 leading-relaxed">
                  Your 15-minute 1:1 with Toriano is locked in. You'll receive scheduling details by email.
                </p>
              </div>
            )}
          </motion.div>
        )}

        <p className="mt-10 text-center text-[10px] tracking-[0.3em] uppercase text-[#f7f1e5]/35" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          Wolfkeeper University · torianosanzone.com
        </p>
      </div>
    </div>
  );
}
